import React from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Space,
  Table,
  Tag,
  Typography,
  Popconfirm,
} from 'antd';
import { RollbackOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { EligibleItem, n5p1Api } from '../../services/n5p1Api';
import { useN5P1Reversal } from './hooks/useN5P1Reversal';
import { BatchAcceptanceBridge, pwn5p1f1AcceptanceManifest } from '../../features/acceptance';
import './N5P1Form.css';

const { Title, Text } = Typography;

function renderStatus(status: string): React.ReactNode {
  if (status === 'P') return <Tag color="blue">已核准</Tag>;
  if (status === 'N') return <Tag color="red">已沖銷</Tag>;
  return <Tag color="default">{status || '未知'}</Tag>;
}

const N5P1Form: React.FC = () => {
  const {
    loading,
    executing,
    items,
    total,
    selectedKeys,
    filter,
    setFilter,
    setSelectedKeys,
    fetchEligible,
    batchExecute,
  } = useN5P1Reversal();

  const columns: ColumnsType<EligibleItem> = [
    {
      title: '買收單號',
      dataIndex: 'fn210_01',
      key: 'fn210_01',
      width: 110,
      render: (text: string) => <Text code>{text}</Text>,
    },
    {
      title: '日期',
      dataIndex: 'fn210_11',
      key: 'fn210_11',
      width: 110,
      render: (date: string) => date || '\u2014',
    },
    {
      title: '供應商',
      dataIndex: 'fn210_15',
      key: 'fn210_15',
      ellipsis: true,
    },
    {
      title: '版本',
      dataIndex: 'fn210_90',
      key: 'fn210_90',
      width: 60,
      align: 'center',
    },
    {
      title: '狀態',
      dataIndex: 'fn210_80',
      key: 'fn210_80',
      width: 90,
      align: 'center',
      render: renderStatus,
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedKeys,
    onChange: (keys: React.Key[]) => setSelectedKeys(keys as string[]),
  };

  const handleSearch = () => { fetchEligible(); };

  const handleFilterChange = (field: 'startNo' | 'endNo', value: string) => {
    setFilter({ ...filter, [field]: value });
  };

  const handleStartBlur = () => {
    if (filter.startNo && !filter.endNo) {
      const autoEnd = (filter.startNo + 'zzzzzzzz').substring(0, 7);
      setFilter({ ...filter, endNo: autoEnd });
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <Title level={4} style={{ marginBottom: 16 }}>客訴工廠買收交運沖銷 (pwn5p1f1)</Title>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Form layout="inline" size="small">
          <Form.Item label="單號起始">
            <Input
              value={filter.startNo}
              onChange={(e) => handleFilterChange('startNo', e.target.value)}
              onBlur={handleStartBlur}
              placeholder="例：FN20001"
              style={{ width: 120 }}
              maxLength={7}
              onPressEnter={handleSearch}
            />
          </Form.Item>
          <Form.Item label="單號結束">
            <Input
              value={filter.endNo}
              onChange={(e) => handleFilterChange('endNo', e.target.value)}
              placeholder="例：FN29999"
              style={{ width: 120 }}
              maxLength={7}
              onPressEnter={handleSearch}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              loading={loading}
            >
              查詢
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Text type="secondary">
                共 <strong>{total}</strong> 筆待沖銷
              </Text>
              {selectedKeys.length > 0 && (
                <Text type="secondary">
                  已勾選 <strong>{selectedKeys.length}</strong> 筆
                </Text>
              )}
            </Space>
          </Col>
          <Col>
            <Popconfirm
              title={`確認沖銷 ${selectedKeys.length} 筆買收單？沖銷後將回復 FM030/FG03 庫存，無法還原。`}
              onConfirm={batchExecute}
              okText="確認沖銷"
              cancelText="取消"
              disabled={selectedKeys.length === 0}
              okButtonProps={{ danger: true }}
            >
              <Button
                danger
                icon={<RollbackOutlined />}
                loading={executing}
                disabled={selectedKeys.length === 0}
              >
                批量沖銷
              </Button>
            </Popconfirm>
          </Col>
        </Row>
      </Card>

      <Table<EligibleItem>
        rowKey="fn210_01"
        columns={columns}
        dataSource={items}
        loading={loading}
        rowSelection={rowSelection}
        size="small"
        pagination={{
          pageSize: 50,
          showSizeChanger: false,
          showTotal: (t) => `共 ${t} 筆`,
        }}
        scroll={{ x: 550 }}
        locale={{ emptyText: '無待沖銷資料' }}
      />
      <BatchAcceptanceBridge
        manifest={pwn5p1f1AcceptanceManifest}
        records={items}
        selectedKeys={selectedKeys}
        keyOf={(item) => item.fn210_01}
        queryExact={(key) => n5p1Api.search({ startNo: key, endNo: key })}
        lastApiResponse={items}
        loading={loading || executing}
        onAutoFill={() => items[0] && setSelectedKeys([items[0].fn210_01])}
        autoFillDisabled={items.length === 0}
        screenDraft={filter}
      />
    </div>
  );
};

export default N5P1Form;
